import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
	ArrowLeft,
	Save,
	Building2,
	CheckCircle,
	Link2,
	AlertTriangle,
	Info,
	Plus,
	X,
	Sparkles,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { useToast } from "@/components/ui/use-toast";
import { adminApiUrl } from "@/config";
import * as api from "@/services/api";
import {
	DependencyWarningDialog,
} from "@/components/features/DependencyWarningDialog";

// Fetch functions
async function fetchPlan(id: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/plans/${id}`), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function fetchFeatures() {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/plans/features"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data;
}

async function createPlan(planData: any) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/plans"), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(planData),
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

async function updatePlan(id: string, planData: any) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/plans/${id}`), {
		method: "PUT",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(planData),
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

async function addFeaturesToPlan(id: string, featureIds: string[]) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/plans/${id}/features`), {
		method: "POST",
		headers: {
			Authorization: `Bearer ${token}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify({ featureIds }),
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

async function removeFeatureFromPlan(planId: string, featureId: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/plans/${planId}/features/${featureId}`), {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data.data;
}

interface PendingFeatureToggle {
	featureId: string;
	featureCode: string;
	isAdding: boolean;
}

export function PlanEditor() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const isNew = id === "new";

	const [formData, setFormData] = useState({
		name: "",
		displayName: "",
		description: "",
		highlights: [] as string[],
		monthlyPrice: 0,
		yearlyPrice: 0,
		prices: {
			usd: { monthly: 0, yearly: 0 },
			aud: { monthly: 0, yearly: 0 },
			gbp: { monthly: 0, yearly: 0 },
			eur: { monthly: 0, yearly: 0 },
		},
		order: 0,
		isActive: true,
		trialDays: 14,
		maxUsers: null as number | null,
		stripePriceIds: { monthly: "", yearly: "" },
		screenshotMinInterval: 30,
		screenshotMaxQuality: "low" as "low" | "medium" | "high",
		screenshotRetentionDays: 7,
		dataRetentionDays: 7,
		storageQuotaMb: 100,
		trackUploads: false,
		trackDownloads: false,
		supportTier: "community" as "community" | "email" | "priority" | "dedicated",
		supportSLA: "none" as "none" | "2-day" | "1-day" | "4-hour",
	});
	const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);

	// Dialog state for dependency warnings
	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState<"enable" | "disable">("enable");
	const [pendingToggle, setPendingToggle] = useState<PendingFeatureToggle | null>(null);
	const [autoGrantedDeps, setAutoGrantedDeps] = useState<
		api.AutoGrantedPermission[]
	>([]);
	const [affectedDependents, setAffectedDependents] = useState<
		Array<{ feature: string; featureName: string; reason?: string }>
	>([]);

	const { data: planData, isLoading: planLoading } = useQuery({
		queryKey: ["admin-plan", id],
		queryFn: () => fetchPlan(id!),
		enabled: !isNew && !!id,
	});

	const { data: featuresData, isLoading: featuresLoading } = useQuery({
		queryKey: ["admin-features"],
		queryFn: fetchFeatures,
	});

	// Fetch dependency information
	const { data: dependencyInfo } = useQuery({
		queryKey: ["features-dependency-info"],
		queryFn: api.getFeaturesWithDependencyInfo,
	});

	// Build a map from feature ID to feature code
	const featureIdToCode = useMemo(() => {
		const map = new Map<string, string>();
		if (featuresData?.features) {
			for (const f of featuresData.features) {
				map.set(f._id, f.code);
			}
		}
		return map;
	}, [featuresData]);

	// Build a map from feature code to feature data
	const featureCodeToData = useMemo(() => {
		const map = new Map<string, any>();
		if (featuresData?.features) {
			for (const f of featuresData.features) {
				map.set(f.code?.toLowerCase(), f);
			}
		}
		return map;
	}, [featuresData]);

	// Get selected feature codes (for dependency checking)
	const selectedFeatureCodes = useMemo(() => {
		return selectedFeatures
			.map((id) => featureIdToCode.get(id)?.toLowerCase())
			.filter(Boolean) as string[];
	}, [selectedFeatures, featureIdToCode]);

	// Helper to get feature name from code
	const getFeatureName = (code: string): string => {
		const feature = featureCodeToData.get(code?.toLowerCase());
		return feature?.name || code;
	};

	// Helper to get feature ID from code
	const getFeatureIdFromCode = (code: string): string | undefined => {
		const feature = featureCodeToData.get(code?.toLowerCase());
		return feature?._id;
	};

	// Check dependencies for a feature
	const getFeatureDependencyInfo = (featureCode: string) => {
		const code = featureCode?.toLowerCase();
		const deps = dependencyInfo?.dependencyMap[code] || [];
		const dependents = dependencyInfo?.reverseDependencyMap[code] || [];

		// Calculate missing dependencies (not in selected features)
		const missingDeps = deps.filter(
			(dep) => !selectedFeatureCodes.includes(dep.feature.toLowerCase()),
		);

		// Calculate enabled dependents (in selected features)
		const enabledDependents = dependents.filter((depCode) =>
			selectedFeatureCodes.includes(depCode.toLowerCase()),
		);

		return {
			hasDependencies: deps.length > 0,
			dependencies: deps,
			dependencyCount: deps.length,
			missingDependencies: missingDeps,
			hasDependents: dependents.length > 0,
			dependents,
			dependentCount: dependents.length,
			enabledDependents,
		};
	};

	// Load plan data
	useEffect(() => {
		if (planData?.plan) {
			const plan = planData.plan;
			setFormData({
				name: plan.name || "",
				displayName: plan.displayName || "",
				description: plan.description || "",
				highlights: plan.highlights || [],
				monthlyPrice: plan.monthlyPrice || 0,
				yearlyPrice: plan.yearlyPrice || 0,
				prices: plan.prices || {
					usd: { monthly: plan.monthlyPrice || 0, yearly: plan.yearlyPrice || 0 },
					aud: { monthly: 0, yearly: 0 },
					gbp: { monthly: 0, yearly: 0 },
					eur: { monthly: 0, yearly: 0 },
				},
				order: plan.order || 0,
				isActive: plan.isActive ?? true,
				trialDays: plan.trialDays ?? 14,
				maxUsers: plan.maxUsers ?? null,
				stripePriceIds: plan.stripePriceIds ?? { monthly: "", yearly: "" },
				screenshotMinInterval: plan.screenshotMinInterval ?? 30,
				screenshotMaxQuality: plan.screenshotMaxQuality ?? "low",
				screenshotRetentionDays: plan.screenshotRetentionDays ?? 7,
				dataRetentionDays: plan.dataRetentionDays ?? 7,
				storageQuotaMb: plan.storageQuotaMb ?? 100,
				trackUploads: plan.trackUploads ?? false,
				trackDownloads: plan.trackDownloads ?? false,
				supportTier: plan.supportTier ?? "community",
				supportSLA: plan.supportSLA ?? "none",
			});
			setSelectedFeatures(plan.features?.map((f: any) => f._id || f) || []);
		}
	}, [planData]);

	const createMutation = useMutation({
		mutationFn: (data: any) => createPlan(data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
			toast({
				title: "Plan created",
				description: "The plan has been created successfully.",
			});
			navigate(`/plans/${data._id}`);
		},
		onError: (error: Error) => {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const updateMutation = useMutation({
		mutationFn: (data: any) => updatePlan(id!, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
			queryClient.invalidateQueries({ queryKey: ["admin-plan", id] });
			toast({
				title: "Plan updated",
				description: "The plan has been updated successfully.",
			});
		},
		onError: (error: Error) => {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const addFeaturesMutation = useMutation({
		mutationFn: (featureIds: string[]) => addFeaturesToPlan(id!, featureIds),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-plan", id] });
			toast({
				title: "Features added",
				description: "Features have been added to the plan.",
			});
		},
		onError: (error: Error) => {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const removeFeatureMutation = useMutation({
		mutationFn: (featureId: string) => removeFeatureFromPlan(id!, featureId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-plan", id] });
			toast({
				title: "Feature removed",
				description: "The feature has been removed from the plan.",
			});
		},
		onError: (error: Error) => {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		// Clean prices: strip zero-valued non-USD entries so frontend falls back to USD
		const cleanedPrices = { ...formData.prices };
		for (const key of ["aud", "gbp", "eur"] as const) {
			if (
				(!cleanedPrices[key]?.monthly || cleanedPrices[key]?.monthly === 0) &&
				(!cleanedPrices[key]?.yearly || cleanedPrices[key]?.yearly === 0)
			) {
				delete cleanedPrices[key];
			}
		}
		const payload = { ...formData, prices: cleanedPrices };
		if (isNew) {
			createMutation.mutate({ ...payload, features: selectedFeatures });
		} else {
			updateMutation.mutate(payload);
		}
	};

	const handleToggleFeature = (featureId: string) => {
		const featureCode = featureIdToCode.get(featureId) || "";
		const isSelected = selectedFeatures.includes(featureId);

		if (!isSelected) {
			// Adding feature - check for missing dependencies
			const depInfo = getFeatureDependencyInfo(featureCode);
			if (depInfo.missingDependencies.length > 0) {
				setAutoGrantedDeps(
					depInfo.missingDependencies.map((dep) => ({
						feature: dep.feature,
						action: dep.action,
						requiredBy: featureCode,
						reason: dep.reason,
					})),
				);
				setDialogMode("enable");
				setPendingToggle({ featureId, featureCode, isAdding: true });
				setDialogOpen(true);
				return;
			}
		} else {
			// Removing feature - check if other selected features depend on this
			const depInfo = getFeatureDependencyInfo(featureCode);
			if (depInfo.enabledDependents.length > 0) {
				setAffectedDependents(
					depInfo.enabledDependents.map((depCode) => {
						const depFeatureDeps =
							dependencyInfo?.dependencyMap[depCode.toLowerCase()] || [];
						const reasonDep = depFeatureDeps.find(
							(d) => d.feature.toLowerCase() === featureCode.toLowerCase(),
						);
						return {
							feature: depCode,
							featureName: getFeatureName(depCode),
							reason: reasonDep?.reason,
						};
					}),
				);
				setDialogMode("disable");
				setPendingToggle({ featureId, featureCode, isAdding: false });
				setDialogOpen(true);
				return;
			}
		}

		// No dependency issues, proceed directly
		executeFeatureToggle(featureId, isSelected);
	};

	const executeFeatureToggle = (featureId: string, wasSelected: boolean) => {
		if (isNew) {
			setSelectedFeatures((prev) =>
				wasSelected
					? prev.filter((f) => f !== featureId)
					: [...prev, featureId],
			);
		} else {
			if (wasSelected) {
				removeFeatureMutation.mutate(featureId);
				setSelectedFeatures((prev) => prev.filter((f) => f !== featureId));
			} else {
				addFeaturesMutation.mutate([featureId]);
				setSelectedFeatures((prev) => [...prev, featureId]);
			}
		}
	};

	const handleDialogConfirm = () => {
		if (!pendingToggle) return;

		const wasSelected = selectedFeatures.includes(pendingToggle.featureId);

		if (pendingToggle.isAdding && autoGrantedDeps.length > 0) {
			// Add the feature and all its missing dependencies
			const featureIdsToAdd = [pendingToggle.featureId];
			for (const dep of autoGrantedDeps) {
				const depId = getFeatureIdFromCode(dep.feature);
				if (depId && !selectedFeatures.includes(depId)) {
					featureIdsToAdd.push(depId);
				}
			}

			if (isNew) {
				setSelectedFeatures((prev) => [...prev, ...featureIdsToAdd]);
			} else {
				addFeaturesMutation.mutate(featureIdsToAdd);
				setSelectedFeatures((prev) => [...prev, ...featureIdsToAdd]);
			}
		} else {
			// Just toggle the single feature
			executeFeatureToggle(pendingToggle.featureId, wasSelected);
		}

		// Reset dialog state
		setDialogOpen(false);
		setPendingToggle(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	const handleDialogCancel = () => {
		setDialogOpen(false);
		setPendingToggle(null);
		setAutoGrantedDeps([]);
		setAffectedDependents([]);
	};

	if (planLoading || featuresLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center gap-4">
				<Button variant="ghost" size="sm" onClick={() => navigate("/plans")}>
					<ArrowLeft className="w-4 h-4 mr-2" />
					Back to Plans
				</Button>
			</div>

			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">
						{isNew
							? "Create Plan"
							: `Edit: ${formData.displayName || formData.name}`}
					</h1>
					<p className="text-gray-500 mt-1">
						{isNew
							? "Create a new subscription plan"
							: "Update plan details and features"}
					</p>
				</div>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Plan Details Form */}
				<div className="lg:col-span-2">
					<Card>
						<CardHeader>
							<CardTitle>Plan Details</CardTitle>
						</CardHeader>
						<CardContent>
							<form onSubmit={handleSubmit} className="space-y-6">
								<div className="grid grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Plan Name (ID)
										</label>
										<Input
											value={formData.name}
											onChange={(e) =>
												setFormData({ ...formData, name: e.target.value })
											}
											placeholder="e.g., professional"
											disabled={!isNew}
											required
										/>
										<p className="text-xs text-gray-500 mt-1">
											Unique identifier, cannot be changed after creation
										</p>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Display Name
										</label>
										<Input
											value={formData.displayName}
											onChange={(e) =>
												setFormData({
													...formData,
													displayName: e.target.value,
												})
											}
											placeholder="e.g., Professional"
											required
										/>
									</div>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Description
									</label>
									<textarea
										value={formData.description}
										onChange={(e) =>
											setFormData({ ...formData, description: e.target.value })
										}
										className="w-full px-3 py-2 border rounded-md text-sm"
										rows={3}
										placeholder="Plan description..."
									/>
								</div>

								{/* Plan Highlights */}
								<div className="border rounded-lg p-4 bg-gray-50">
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center gap-2">
											<Sparkles className="w-4 h-4 text-purple-500" />
											<label className="text-sm font-medium text-gray-700">
												Plan Highlights
											</label>
										</div>
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() =>
												setFormData({
													...formData,
													highlights: [...formData.highlights, ""],
												})
											}
										>
											<Plus className="w-3 h-3 mr-1" />
											Add
										</Button>
									</div>
									<p className="text-xs text-gray-500 mb-3">
										Marketing-friendly bullet points shown on pricing pages
										(e.g., "Up to 50 employees", "Priority support")
									</p>
									{formData.highlights.length === 0 ? (
										<p className="text-sm text-gray-400 text-center py-2">
											No highlights added yet
										</p>
									) : (
										<div className="space-y-2">
											{formData.highlights.map((highlight, index) => (
												<div
													key={index}
													className="flex items-center gap-2"
												>
													<span className="text-xs text-gray-400 w-5 text-center">
														{index + 1}
													</span>
													<Input
														value={highlight}
														onChange={(e) => {
															const updated = [...formData.highlights];
															updated[index] = e.target.value;
															setFormData({
																...formData,
																highlights: updated,
															});
														}}
														placeholder="e.g., Up to 50 employees"
														className="flex-1"
													/>
													<Button
														type="button"
														variant="ghost"
														size="sm"
														onClick={() => {
															const updated = formData.highlights.filter(
																(_, i) => i !== index,
															);
															setFormData({
																...formData,
																highlights: updated,
															});
														}}
													>
														<X className="w-3 h-3 text-red-500" />
													</Button>
												</div>
											))}
										</div>
									)}
								</div>

								{/* Multi-Currency Pricing */}
								<div className="space-y-3">
									<h4 className="text-sm font-semibold text-gray-900">
										Pricing by Currency
									</h4>
									{(
										[
											{ key: "usd", label: "USD ($)", symbol: "$" },
											{ key: "aud", label: "AUD (A$)", symbol: "A$" },
											{ key: "gbp", label: "GBP (£)", symbol: "£" },
											{ key: "eur", label: "EUR (€)", symbol: "€" },
										] as const
									).map((cur) => (
										<div
											key={cur.key}
											className="grid grid-cols-3 gap-4 items-end"
										>
											<label className="block text-sm font-medium text-gray-600 self-center">
												{cur.label}
											</label>
											<div>
												{cur.key === "usd" && (
													<label className="block text-xs text-gray-500 mb-1">
														Monthly
													</label>
												)}
												<Input
													type="number"
													step="0.01"
													min={0}
													placeholder="Monthly"
													value={
														formData.prices[cur.key]?.monthly || ""
													}
													onChange={(e) => {
														const val = Number(e.target.value);
														setFormData({
															...formData,
															...(cur.key === "usd"
																? { monthlyPrice: val }
																: {}),
															prices: {
																...formData.prices,
																[cur.key]: {
																	...formData.prices[cur.key],
																	monthly: val,
																},
															},
														});
													}}
												/>
											</div>
											<div>
												{cur.key === "usd" && (
													<label className="block text-xs text-gray-500 mb-1">
														Yearly
													</label>
												)}
												<Input
													type="number"
													step="0.01"
													min={0}
													placeholder="Yearly"
													value={
														formData.prices[cur.key]?.yearly || ""
													}
													onChange={(e) => {
														const val = Number(e.target.value);
														setFormData({
															...formData,
															...(cur.key === "usd"
																? { yearlyPrice: val }
																: {}),
															prices: {
																...formData.prices,
																[cur.key]: {
																	...formData.prices[cur.key],
																	yearly: val,
																},
															},
														});
													}}
												/>
											</div>
										</div>
									))}
									<p className="text-xs text-gray-400">
										USD is required. Other currencies are optional (falls back to
										USD).
									</p>
								</div>

								{/* Order field */}
								<div className="w-32">
									<label className="block text-sm font-medium text-gray-700 mb-1">
										Order
									</label>
									<Input
										type="number"
										value={formData.order}
										onChange={(e) =>
											setFormData({
												...formData,
												order: Number(e.target.value),
											})
										}
										min={0}
									/>
								</div>

								<div className="flex items-center gap-2">
									<input
										type="checkbox"
										id="isActive"
										checked={formData.isActive}
										onChange={(e) =>
											setFormData({ ...formData, isActive: e.target.checked })
										}
										className="rounded border-gray-300"
									/>
									<label htmlFor="isActive" className="text-sm">
										Active (available for selection)
									</label>
								</div>

								{/* Trial Days */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
									<input
										type="number"
										min={0}
										max={365}
										value={formData.trialDays}
										onChange={(e) => setFormData({ ...formData, trialDays: parseInt(e.target.value) || 0 })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										placeholder="14"
									/>
									<p className="mt-1 text-xs text-gray-500">Number of free trial days for new signups on this plan</p>
								</div>

								{/* Max Users */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
									<input
										type="number"
										min={0}
										value={formData.maxUsers ?? ""}
										onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value === "" ? null : parseInt(e.target.value) })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
										placeholder="Leave empty for unlimited"
									/>
									<p className="mt-1 text-xs text-gray-500">Maximum users allowed on this plan. Leave empty for unlimited.</p>
								</div>

								{/* Stripe Price IDs */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Stripe Monthly Price ID</label>
									<input
										type="text"
										value={formData.stripePriceIds.monthly}
										onChange={(e) => setFormData({ ...formData, stripePriceIds: { ...formData.stripePriceIds, monthly: e.target.value } })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
										placeholder="price_..."
									/>
								</div>
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Stripe Yearly Price ID</label>
									<input
										type="text"
										value={formData.stripePriceIds.yearly}
										onChange={(e) => setFormData({ ...formData, stripePriceIds: { ...formData.stripePriceIds, yearly: e.target.value } })}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
										placeholder="price_..."
									/>
								</div>

								{/* Plan Limits Section */}
								<div className="pt-4 border-t mt-4">
									<h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
										Plan Limits & Restrictions
									</h3>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Screenshot Min Interval (minutes)</label>
											<input
												type="number"
												min={1}
												max={1440}
												value={formData.screenshotMinInterval}
												onChange={(e) => setFormData({ ...formData, screenshotMinInterval: parseInt(e.target.value) || 30 })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												placeholder="30"
											/>
											<p className="mt-1 text-xs text-gray-500">Minimum screenshot interval this plan allows</p>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Screenshot Max Quality</label>
											<select
												value={formData.screenshotMaxQuality}
												onChange={(e) => setFormData({ ...formData, screenshotMaxQuality: e.target.value as "low" | "medium" | "high" })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
											>
												<option value="low">Low (720p)</option>
												<option value="medium">Medium (1080p)</option>
												<option value="high">High (1080p+)</option>
											</select>
											<p className="mt-1 text-xs text-gray-500">Maximum screenshot quality allowed on this plan</p>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Screenshot Retention (days)</label>
											<input
												type="number"
												min={1}
												max={730}
												value={formData.screenshotRetentionDays}
												onChange={(e) => setFormData({ ...formData, screenshotRetentionDays: parseInt(e.target.value) || 7 })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												placeholder="7"
											/>
											<p className="mt-1 text-xs text-gray-500">Days screenshots are retained before auto-deletion</p>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Data Retention (days)</label>
											<input
												type="number"
												min={1}
												max={730}
												value={formData.dataRetentionDays}
												onChange={(e) => setFormData({ ...formData, dataRetentionDays: parseInt(e.target.value) || 7 })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												placeholder="7"
											/>
											<p className="mt-1 text-xs text-gray-500">Days activity data is retained</p>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Storage Quota (MB)</label>
											<input
												type="number"
												min={0}
												max={1048576}
												value={formData.storageQuotaMb}
												onChange={(e) => setFormData({ ...formData, storageQuotaMb: parseInt(e.target.value) || 0 })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
												placeholder="100"
											/>
											<p className="mt-1 text-xs text-gray-500">Total project/task attachment storage allowed per org (MB)</p>
										</div>
										<div className="flex items-center gap-3 py-2">
											<input
												type="checkbox"
												id="trackUploads"
												checked={formData.trackUploads}
												onChange={(e) => setFormData({ ...formData, trackUploads: e.target.checked })}
												className="rounded border-gray-300 h-4 w-4"
											/>
											<div>
												<label htmlFor="trackUploads" className="text-sm font-medium text-gray-700">Track File Uploads</label>
												<p className="text-xs text-gray-500">Enable cloud file upload detection (Chrome extension)</p>
											</div>
										</div>
										<div className="flex items-center gap-3 py-2">
											<input
												type="checkbox"
												id="trackDownloads"
												checked={formData.trackDownloads}
												onChange={(e) => setFormData({ ...formData, trackDownloads: e.target.checked })}
												className="rounded border-gray-300 h-4 w-4"
											/>
											<div>
												<label htmlFor="trackDownloads" className="text-sm font-medium text-gray-700">Track File Downloads</label>
												<p className="text-xs text-gray-500">Enable download tracking (Chrome extension)</p>
											</div>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Support Tier</label>
											<select
												value={formData.supportTier}
												onChange={(e) => setFormData({ ...formData, supportTier: e.target.value as "community" | "email" | "priority" | "dedicated" })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
											>
												<option value="community">Community (Help Center)</option>
												<option value="email">Email Support</option>
												<option value="priority">Priority (Email + Chat)</option>
												<option value="dedicated">Dedicated Account Manager</option>
											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">Support SLA</label>
											<select
												value={formData.supportSLA}
												onChange={(e) => setFormData({ ...formData, supportSLA: e.target.value as "none" | "2-day" | "1-day" | "4-hour" })}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
											>
												<option value="none">None</option>
												<option value="2-day">2 Business Days</option>
												<option value="1-day">1 Business Day</option>
												<option value="4-hour">4 Hours</option>
											</select>
										</div>
									</div>
								</div>

								<div className="flex justify-end gap-2 pt-4 border-t">
									<Button
										type="button"
										variant="outline"
										onClick={() => navigate("/plans")}
									>
										Cancel
									</Button>
									<Button
										type="submit"
										disabled={
											createMutation.isPending || updateMutation.isPending
										}
									>
										<Save className="w-4 h-4 mr-2" />
										{isNew ? "Create Plan" : "Save Changes"}
									</Button>
								</div>
							</form>
						</CardContent>
					</Card>
				</div>

				{/* Plan Stats (Edit mode only) */}
				{!isNew && planData && (
					<div>
						<Card>
							<CardHeader>
								<CardTitle>Statistics</CardTitle>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<Building2 className="w-4 h-4 text-gray-400" />
										<span className="text-sm">Organizations</span>
									</div>
									<span className="font-medium">
										{planData.totalOrganizations || 0}
									</span>
								</div>
								<div className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<CheckCircle className="w-4 h-4 text-gray-400" />
										<span className="text-sm">Features</span>
									</div>
									<span className="font-medium">{selectedFeatures.length}</span>
								</div>
							</CardContent>
						</Card>

						{/* Recent Organizations */}
						{planData.organizations && planData.organizations.length > 0 && (
							<Card className="mt-4">
								<CardHeader>
									<CardTitle className="text-base">
										Recent Organizations
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-2">
										{planData.organizations.slice(0, 5).map((org: any) => (
											<div
												key={org._id}
												className="flex items-center justify-between text-sm"
											>
												<span className="truncate">{org.name}</span>
												<Badge
													className={cn(
														org.status === "active"
															? "bg-green-100 text-green-700"
															: "bg-gray-100 text-gray-700",
													)}
												>
													{org.status}
												</Badge>
											</div>
										))}
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				)}
			</div>

			{/* Dependency Info Banner */}
			{dependencyInfo && (
				<Card className="border-blue-200 bg-blue-50">
					<CardContent className="py-3">
						<div className="flex items-center gap-2 text-sm text-blue-700">
							<Info className="w-4 h-4" />
							<span>
								<strong>Dependency Validation Active:</strong> Features with
								dependencies will show indicators. Missing dependencies will be
								automatically suggested when adding features.
							</span>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Features Section */}
			<Card>
				<CardHeader>
					<CardTitle>Features</CardTitle>
				</CardHeader>
				<CardContent>
					{featuresData?.byCategory ? (
						<div className="space-y-6">
							{Object.entries(featuresData.byCategory).map(
								([category, features]: [string, any]) => (
									<div key={category}>
										<h4 className="font-medium text-gray-900 mb-3 capitalize">
											{category.replace(/_/g, " ")}
										</h4>
										<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
											{features.map((feature: any) => {
												const isSelected = selectedFeatures.includes(
													feature._id,
												);
												const depInfo = getFeatureDependencyInfo(feature.code);
												const hasMissingDeps =
													!isSelected &&
													depInfo.missingDependencies.length > 0;

												return (
													<div
														key={feature._id}
														onClick={() => handleToggleFeature(feature._id)}
														className={cn(
															"flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors relative",
															isSelected
																? "border-blue-500 bg-blue-50"
																: "border-gray-200 hover:bg-gray-50",
														)}
													>
														<div
															className={cn(
																"w-5 h-5 rounded flex items-center justify-center flex-shrink-0",
																isSelected
																	? "bg-blue-500 text-white"
																	: "bg-gray-200",
															)}
														>
															{isSelected && (
																<CheckCircle className="w-4 h-4" />
															)}
														</div>
														<div className="flex-1 min-w-0">
															<div className="flex items-center gap-1">
																<p className="text-sm font-medium truncate">
																	{feature.name}
																</p>
																{depInfo.hasDependencies && (
																	<Badge
																		variant="outline"
																		className={cn(
																			"text-[9px] px-1 py-0",
																			hasMissingDeps
																				? "border-yellow-400 text-yellow-600"
																				: "border-green-400 text-green-600",
																		)}
																		title={
																			hasMissingDeps
																				? `Missing ${depInfo.missingDependencies.length} dependency(ies)`
																				: "All dependencies satisfied"
																		}
																	>
																		<Link2 className="w-2.5 h-2.5 mr-0.5" />
																		{depInfo.dependencyCount}
																	</Badge>
																)}
																{depInfo.hasDependents && isSelected && (
																	<Badge
																		variant="outline"
																		className="text-[9px] px-1 py-0 border-orange-400 text-orange-600"
																		title={`${depInfo.enabledDependents.length} selected feature(s) depend on this`}
																	>
																		<AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
																		{depInfo.enabledDependents.length}
																	</Badge>
																)}
															</div>
															<p className="text-xs text-gray-500 truncate">
																{feature.code}
															</p>
															{/* Show missing deps hint */}
															{hasMissingDeps && (
																<p className="text-[10px] text-yellow-600 mt-1 truncate">
																	Requires:{" "}
																	{depInfo.missingDependencies
																		.map((d) => d.feature)
																		.join(", ")}
																</p>
															)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								),
							)}
						</div>
					) : (
						<p className="text-center text-gray-500 py-4">
							No features available
						</p>
					)}
				</CardContent>
			</Card>

			{/* Legend */}
			<Card>
				<CardContent className="py-3">
					<div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
						<div className="flex items-center gap-1">
							<Badge
								variant="outline"
								className="text-[9px] border-green-400 text-green-600"
							>
								<Link2 className="w-2.5 h-2.5 mr-0.5" />N
							</Badge>
							<span>= All N dependencies satisfied</span>
						</div>
						<div className="flex items-center gap-1">
							<Badge
								variant="outline"
								className="text-[9px] border-yellow-400 text-yellow-600"
							>
								<Link2 className="w-2.5 h-2.5 mr-0.5" />N
							</Badge>
							<span>= Has missing dependencies</span>
						</div>
						<div className="flex items-center gap-1">
							<Badge
								variant="outline"
								className="text-[9px] border-orange-400 text-orange-600"
							>
								<AlertTriangle className="w-2.5 h-2.5 mr-0.5" />N
							</Badge>
							<span>= N features depend on this</span>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Dependency Warning Dialog */}
			<DependencyWarningDialog
				open={dialogOpen}
				onOpenChange={setDialogOpen}
				mode={dialogMode}
				featureName={pendingToggle ? getFeatureName(pendingToggle.featureCode) : ""}
				autoGrantedDependencies={autoGrantedDeps}
				affectedDependents={affectedDependents}
				onConfirm={handleDialogConfirm}
				onCancel={handleDialogCancel}
				isLoading={addFeaturesMutation.isPending || removeFeatureMutation.isPending}
			/>
		</div>
	);
}
